/**
 * The rate-card form (POST / PATCH /well-servicing/rates).
 *
 * Currency is NEVER sent — a rate is in the company's currency, stamped by the
 * server (`currency_not_settable`); it is shown read-only. The fields that
 * apply to one kind only are shown — and sent — for that kind only:
 *
 *   crew_extra / subsistence  a crew position       (`crew_position_not_people`)
 *   equipment                 EXACTLY ONE of a unit or a type — there is no
 *                             "any unit" equipment rate, so the rig carrier is
 *                             not billed twice (`equipment_unit_or_type`)
 *   fuel                      per volume, in L or gal (`fuel_unit`)
 *   third_party               NO RATE — `pricing` at cost or a markup %; the
 *                             vendor's cost is entered on each ticket line
 *                             (`third_party_rate`, `markup_not_markup`)
 *   per_volume (a unit)       the volume unit        (`volume_not_per_volume`)
 */
import React, { useEffect, useState } from 'react';
import { blankToNull, toInput } from '../../../lib/well-servicing';
import type { WsRate, WsRateWrite, WsTicketCatalog } from '../../../types/ws-tickets';
import { WS_DATE_INPUT, WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsField, WsFormError } from '../WsBits';
import { PfFormGrid as WsFormGrid } from '../../pipeline-facility/digs/DigBits';

export interface WsRateFormOptions {
  customers: { id: string; name: string }[];
  equipment: { id: string; name: string; unitNumber?: string | null }[];
  equipmentTypes: { id: string; name: string }[];
}

/** The kinds a crew position prices (mirrors the API's WS_PEOPLE_KINDS, lib/ws-rates.ts). */
export const WS_PEOPLE_KINDS = ['crew_extra', 'subsistence'];
/** The volume units a fuel rate may be counted in — the units a tour sheet records fuel in. */
export const WS_FUEL_UNITS = ['L', 'gal'];

export function WsRateForm({
  rate,
  catalog,
  options,
  currency,
  today,
  defaultCustomerId,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  rate?: WsRate | null;
  catalog: WsTicketCatalog | null;
  options: WsRateFormOptions;
  currency: string | null;
  today: string | null;
  defaultCustomerId?: string;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: WsRateWrite) => void;
}) {
  const [code, setCode] = useState(rate?.code ?? '');
  const [name, setName] = useState(rate?.name ?? '');
  const [kind, setKind] = useState(rate?.kind ?? '');
  const [unit, setUnit] = useState(rate?.unit ?? '');
  const [amount, setAmount] = useState(toInput(rate?.rate));
  const [pricing, setPricing] = useState(rate?.pricing ?? '');
  const [markup, setMarkup] = useState(toInput(rate?.markupPct));
  const [volumeUnit, setVolumeUnit] = useState(rate?.volumeUnit ?? '');
  const [customerId, setCustomerId] = useState(rate ? rate.customerId ?? '' : defaultCustomerId ?? '');
  const [crewPosition, setCrewPosition] = useState(rate?.crewPosition ?? '');
  const [equipmentId, setEquipmentId] = useState(rate?.equipmentId ?? '');
  const [equipmentTypeId, setEquipmentTypeId] = useState(rate?.equipmentTypeId ?? '');
  const [from, setFrom] = useState(rate?.effectiveFrom ?? today ?? '');
  const [to, setTo] = useState(rate?.effectiveTo ?? '');
  const [description, setDescription] = useState(rate?.description ?? '');
  const [notes, setNotes] = useState(rate?.notes ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  // The tenant's day arrives with the rates query's meta, usually after the
  // first render; a new rate starts on it unless the office has picked a day.
  useEffect(() => {
    if (!rate && today) setFrom((f) => f || today);
  }, [rate, today]);

  const thirdParty = kind === 'third_party';
  const people = WS_PEOPLE_KINDS.includes(kind);
  const fuel = kind === 'fuel';

  const submit = () => {
    setProblem(null);
    if (!code.trim()) return setProblem('Enter a short code for the rate.');
    if (!name.trim()) return setProblem('Enter the rate’s name.');
    if (!kind || !unit) return setProblem('Pick what kind of charge it is and its unit.');
    if (thirdParty) {
      if (pricing !== 'at_cost' && pricing !== 'markup_pct') return setProblem('Pick how the vendor’s cost is billed: at cost, or cost plus a markup %.');
      if (pricing === 'markup_pct' && (!markup.trim() || !Number.isFinite(Number(markup)) || Number(markup) < 0))
        return setProblem('Enter the markup as a percentage of 0 or more.');
    } else if (!amount.trim() || !Number.isFinite(Number(amount)) || Number(amount) < 0) {
      return setProblem('Enter the rate as a number of 0 or more.');
    }
    if (fuel && (unit !== 'per_volume' || !WS_FUEL_UNITS.includes(volumeUnit.trim())))
      return setProblem('A fuel rate is per volume, in litres (L) or gallons (gal) — the units a tour sheet records fuel in.');
    if (unit === 'per_volume' && !volumeUnit.trim()) return setProblem('Enter the volume unit the rate is counted in (L, gal, m³, …).');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return setProblem('Enter the day the rate starts.');
    if (to && to < from) return setProblem('The end day can’t be before the start day.');
    if (kind === 'equipment' && !!equipmentId === !!equipmentTypeId) return setProblem('An equipment rate prices one unit or one equipment type. Pick exactly one.');
    const body: WsRateWrite = {
      code: code.trim(),
      name: name.trim(),
      kind,
      unit,
      effective_from: from,
      effective_to: to || null,
      customer_id: customerId || null,
      description: blankToNull(description),
      notes: blankToNull(notes),
    };
    // A third-party rate carries no rate of its own (`third_party_rate`); it is never sent.
    if (thirdParty) {
      body.pricing = pricing;
      if (pricing === 'markup_pct') body.markup_pct = markup.trim();
    } else {
      body.rate = amount.trim();
    }
    if (unit === 'per_volume') body.volume_unit = volumeUnit.trim();
    if (people) body.crew_position = crewPosition || null;
    if (kind === 'equipment') {
      body.equipment_id = equipmentId || null;
      body.equipment_type_id = equipmentTypeId || null;
    }
    onSubmit(body);
  };

  return (
    <div data-testid="ws-rate-form" className="space-y-4 px-4 py-4">
      <WsFormError message={problem ?? error} />
      <WsFormGrid>
        <WsField label="Code *" hint="Short, e.g. RIG-HR. Unique per customer card and period.">
          <input className={WS_INPUT} maxLength={30} value={code} onChange={(e) => setCode(e.target.value)} />
        </WsField>
        <WsField label="Name *">
          <input className={WS_INPUT} maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
        </WsField>
        <WsField label="Card" hint="The default card applies to every customer without a rate of their own.">
          <select className={WS_INPUT} aria-label="Card" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Default card (every customer)</option>
            {options.customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </WsField>
        <WsField label="Kind *">
          <select className={WS_INPUT} aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Pick one</option>
            {(catalog?.priceKinds ?? []).map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </WsField>
        <WsField label="Unit *">
          <select className={WS_INPUT} aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
            <option value="">Pick one</option>
            {(catalog?.priceUnits ?? []).map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </WsField>
        {thirdParty ? (
          <>
            <WsField label="Pricing *" hint="A third-party rate has no rate of its own: the vendor’s cost is entered on each ticket line.">
              <select className={WS_INPUT} aria-label="Pricing" value={pricing} onChange={(e) => setPricing(e.target.value)}>
                <option value="">Pick one</option>
                {(catalog?.thirdPartyPricing ?? []).map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </WsField>
            {pricing === 'markup_pct' && (
              <WsField label="Markup % *">
                <input className={WS_INPUT} inputMode="decimal" aria-label="Markup %" value={markup} onChange={(e) => setMarkup(e.target.value)} />
              </WsField>
            )}
          </>
        ) : (
          <WsField label="Rate *">
            <input className={WS_INPUT} inputMode="decimal" aria-label="Rate" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </WsField>
        )}
        <WsField label="Currency" hint="The company’s currency, set in Settings. Not set per rate.">
          <p data-testid="ws-rate-currency" className="flex min-h-11 items-center font-mono text-sm text-white">
            {currency ?? '—'}
          </p>
        </WsField>
        {unit === 'per_volume' &&
          (fuel ? (
            <WsField label="Volume unit *" hint="Fuel is counted in litres or gallons, as the tour sheet records it.">
              <select className={WS_INPUT} aria-label="Volume unit" value={volumeUnit} onChange={(e) => setVolumeUnit(e.target.value)}>
                <option value="">Pick one</option>
                {WS_FUEL_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </WsField>
          ) : (
            <WsField label="Volume unit *" hint="What the rate is counted in, e.g. L, gal or m³.">
              <input className={WS_INPUT} maxLength={20} aria-label="Volume unit" value={volumeUnit} onChange={(e) => setVolumeUnit(e.target.value)} />
            </WsField>
          ))}
        {people && (
          <WsField label="Crew position" hint="Optional. Prices the hands on the tour sheet in this position.">
            <select className={WS_INPUT} aria-label="Crew position" value={crewPosition} onChange={(e) => setCrewPosition(e.target.value)}>
              <option value="">Any position</option>
              {(catalog?.crewPositions ?? []).map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </WsField>
        )}
        {kind === 'equipment' && (
          <>
            <WsField label="Equipment unit" hint="One unit, or one type — exactly one.">
              <select className={WS_INPUT} aria-label="Equipment unit" value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
                <option value="">No single unit</option>
                {options.equipment.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.unitNumber ? `${q.unitNumber} · ${q.name}` : q.name}
                  </option>
                ))}
              </select>
            </WsField>
            <WsField label="Equipment type">
              <select className={WS_INPUT} aria-label="Equipment type" value={equipmentTypeId} onChange={(e) => setEquipmentTypeId(e.target.value)}>
                <option value="">No type</option>
                {options.equipmentTypes.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            </WsField>
          </>
        )}
        <WsField label="Starts *">
          <input className={WS_DATE_INPUT} type="date" aria-label="Starts" value={from} onChange={(e) => setFrom(e.target.value)} />
        </WsField>
        <WsField label="Ends" hint="Blank = no end day.">
          <input className={WS_DATE_INPUT} type="date" aria-label="Ends" value={to} onChange={(e) => setTo(e.target.value)} />
        </WsField>
      </WsFormGrid>
      <WsField label="Description" hint="Optional.">
        <input className={WS_INPUT} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
      </WsField>
      <WsField label="Notes">
        <textarea className={`${WS_INPUT} h-20 py-2`} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </WsField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WS_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="button" className={WS_PRIMARY} onClick={submit} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}
